import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, BookOpen, CheckCircle2, FlaskConical, LayoutDashboard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function StudyMaterialSubjects() {
  const { examId } = useParams();
  const { token } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/study-material/exam/${examId}/subjects`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setSubjects(d.subjects || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [examId, token]);

  const formatExamName = (id) => {
    if (id.startsWith('board_ssc_')) return `Maharashtra SSC - Class ${id.split('_')[2]}`;
    if (id.startsWith('board_hsc_')) return `Maharashtra HSC - Class ${id.split('_')[2]}`;
    if (id.startsWith('board_cbse_')) return `CBSE - Class ${id.split('_')[2]}`;
    return id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };
  const examName = formatExamName(examId);
  const totalTopics = subjects.reduce((a, s) => a + s.topic_count, 0);
  const totalCompleted = subjects.reduce((a, s) => a + s.completed_count, 0);

  const COLORS = [
    'from-blue-500 to-cyan-500', 'from-emerald-500 to-teal-500',
    'from-violet-500 to-purple-500', 'from-amber-500 to-orange-500',
    'from-pink-500 to-rose-500', 'from-indigo-500 to-blue-500',
    'from-red-500 to-rose-500', 'from-teal-500 to-cyan-500',
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="study-subjects">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/study-material" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-hub">
            <ArrowLeft className="w-4 h-4" />Back to Study Material
          </Link>
          <Link to="/dashboard">
            <Button variant="outline" size="sm" data-testid="go-dashboard-btn">
              <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </Link>
        </div>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg">
            <BookOpen className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{examName}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-muted-foreground">{subjects.length} subjects</span>
              <span className="text-sm text-muted-foreground">{totalTopics} topics</span>
              {totalCompleted > 0 && (
                <Badge className="text-xs bg-emerald-500/10 text-emerald-400">
                  <CheckCircle2 className="w-3 h-3 mr-1" />{totalCompleted} completed
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Overall Progress */}
        {totalTopics > 0 && (
          <div className="p-4 rounded-2xl border border-border bg-card mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-muted-foreground">{totalCompleted}/{totalTopics} topics</span>
            </div>
            <Progress value={totalTopics > 0 ? (totalCompleted / totalTopics) * 100 : 0} className="h-2.5" />
          </div>
        )}

        {/* Subject Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {subjects.map((sub, i) => {
            const progress = sub.topic_count > 0 ? (sub.completed_count / sub.topic_count) * 100 : 0;
            return (
              <Link
                key={sub.subject}
                to={`/study-material/${examId}/${encodeURIComponent(sub.subject)}`}
                className="group relative p-5 rounded-2xl border border-border bg-card hover:border-primary/30 hover:shadow-lg transition-all duration-300"
                data-testid={`subject-${sub.subject.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${COLORS[i % COLORS.length]} flex items-center justify-center shrink-0 shadow-md group-hover:scale-105 transition-transform`}>
                    <FlaskConical className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold group-hover:text-primary transition-colors">{sub.subject}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{sub.topic_count} topics</p>
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                        <span>{sub.completed_count} completed</span>
                        <span>{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-1.5" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
